import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { useQuery } from '@apollo/client';
import { useDispatch } from 'react-redux';
import _ from 'lodash';
import PropTypes from 'prop-types';

import dayjs from 'dayjs';
import objectSupport from 'dayjs/plugin/objectSupport';

import {
    Progress,
    GoBackButton,
    LineChartCard,
    LineChartSMACard,
    PieChartCard,
} from '../../components/index';

import {
    InfoCard,
    PatientsEmotional,
    SessionNotesCard,
    TranscribeCard,
} from '../../components/Provider/Report';

import { GET_MEET_REPORT, getGroupReport } from '../../services/provider';
import { transcribe } from '../../services';

import { alertError } from '../../redux/stateActions';
import { providerPageWidth } from '../../constant';

dayjs.extend(objectSupport);

const GroupReport = ({ history, location }) => {
    const dispatch = useDispatch();
    const { id } = useParams();

    const [loading, setLoading] = useState(false);
    const [realTime, setRealTime] = useState([]);
    const [expressionByPatient, setExpressionByPatient] = useState([]);
    const [meetData, setMeetData] = useState({});

    const [reportData, setReportData] = useState([]);
    const [selectedPatient, setSelectedPatient] = useState(null);

    const [summaryData, setSummaryData] = useState([]);

    const [sessionNotes, setSessionNotes] = useState([]);

    const [transcribes, setTranscribes] = useState({});

    const handleGoback = () => {
        if (location.state?.prevPath) {
            const path = {
                pathname: location.state.prevPath,
                state: {
                    tab: location.state.prevTab,
                },
            };
            history.push(path);
        } else {
            history.goBack();
        }
    };

    const fetchReport = async () => {
        setLoading(true);
        try {
            const res = await getGroupReport(id);

            if (res.status === 200) {
                const newExpressionByPatient = [];
                const newReportData = [];

                res.data.groupByPatients.forEach((item, index) => {
                    if (index === 0) {
                        setSelectedPatient(item.patientID);
                    }

                    const reportObj = {
                        patientID: item.patientID,
                        summary: item.summary,
                        realTime: item.realTime,
                    };

                    newReportData.push(reportObj);

                    const expressionObj = {
                        expression: item.byExpression,
                        patient: meetData.patients.find((patient) => patient.id === item.patientID),
                    };
                    newExpressionByPatient.push(expressionObj);
                });

                setReportData(newReportData);
                setExpressionByPatient(newExpressionByPatient);
                setSummaryData(res.data.summary);
            } else {
                throw new Error(res);
            }
        } catch (error) {
            dispatch(alertError('Cannot Get report: ' + error.response.data.message));
        }
        setLoading(false);
    };

    useEffect(() => {
        if (selectedPatient && !_.isEmpty(reportData)) {
            const foundReport = reportData.find((r) => r.patientID === selectedPatient);
            if (foundReport) {
                setRealTime(foundReport.realTime);
            } else {
                dispatch(alertError('Report of this selected client not found'));
            }
        }
    }, [selectedPatient, reportData]);

    useEffect(() => {
        if (id && !_.isEmpty(meetData)) {
            fetchReport();
        }
    }, [id, meetData]);

    /*--------------- Get Meet ---------------*/
    const { data: getMeetData, error: getMeetError } = useQuery(GET_MEET_REPORT, {
        variables: { id: id },
        skip: !id,
    });

    useEffect(() => {
        if (getMeetError) {
            dispatch(alertError(`Cannot get Meet data: ${getMeetError.message}`));
        }

        if (getMeetData && _.isEmpty(getMeetData.meet)) {
            dispatch(alertError(`Meet not found`));
        }

        if (getMeetData && !_.isEmpty(getMeetData.meet)) {
            setMeetData(getMeetData.meet);

            if (getMeetData.meet.sessions.length === 0) {
                dispatch(alertError(`Session not found`));
                return;
            }
            if (getMeetData.meet.sessions.length > 1) {
                dispatch(alertError(`Found more than one session`));
                return;
            }

            setSessionNotes(getMeetData.meet.sessions[0].session_notes);

            if (getMeetData.meet.sessions[0]?.roomId) {
                const users = [];
                users.push(getMeetData.meet.staff.user);
                getMeetData.meet.patients.forEach((patient) => {
                    if (patient?.user) {
                        users.push(patient.user);
                    }
                });

                getTranscribes(getMeetData.meet.sessions[0].roomId, users);
            }
        }
    }, [getMeetError, getMeetData]);

    const getTranscribes = async (roomId, users) => {
        const res = await transcribe(roomId);

        if (!res) return;

        const newTranscribes = {
            status: res.status,
            record: [],
        };
        res.recordTranslate.forEach((record) => {
            const lastItem = _.last(newTranscribes.record);

            if (lastItem && lastItem?.user?.id === record.userId) {
                newTranscribes.record[newTranscribes.record.length - 1].texts.push(record.text);
            } else {
                newTranscribes.record.push({
                    startAt: dayjs(record.startAt).format('HH:mm'),
                    texts: [record.text],
                    user: users.find((user) => user.id === record.userId),
                });
            }
        });

        setTranscribes(newTranscribes);
    };

    return (
        <div
            style={{
                padding: '20px',
                display: 'flex',
                flexDirection: 'column',
                rowGap: '25px',
                maxWidth: providerPageWidth,
                width: '100%',
            }}
        >
            {loading && <Progress />}

            <GoBackButton $text onClick={handleGoback} />

            <InfoCard meet={meetData} />

            <PatientsEmotional
                data={expressionByPatient}
                selectedPatient={selectedPatient}
                setSelectedPatient={setSelectedPatient}
            />

            <LineChartCard data={realTime} />

            <LineChartSMACard data={realTime} />

            <PieChartCard data={summaryData} />

            <SessionNotesCard data={sessionNotes} isGroup />

            <TranscribeCard data={transcribes} />
        </div>
    );
};

GroupReport.propTypes = {
    history: PropTypes.object,
    location: PropTypes.object,
};

export default GroupReport;
